import React from 'react'
import className from 'classnames/bind'
import { SortableHandle } from 'react-sortable-hoc'

import style from './MyhouseSettingItem.module.scss'

import SvgMyhouseSettingRemove from '@Root/ui/images/myhouse/myhouse_setting_remove.svg'
import SvgMyhouseSettingModify from '@Root/ui/images/myhouse/myhouse_setting_modify.svg'
import SvgMyhouseSettingDrag from '@Root/ui/images/myhouse/myhouse_setting_drag.svg'
import SvgMyhouseSettingAuth from '@Root/ui/images/myhouse/myhouse_setting_auth.svg'
import SvgMyhouseSettingAuthArrow from '@Root/ui/images/myhouse/myhouse_setting_auth--arrow.svg'
import { MyhouseLabelType } from '@Root/utils/houseLabelUtils'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import useModal from '@Hooks/myhouse/useModal'
import useSetting from '@Hooks/myhouse/setting/useSetting'
import { AUTH_STATUS, TRADE } from '@Root/contants'
import { MYHOUSE_AUTHENTICATION_CATEGORY_TYPE } from '@Root/contants/myhouseRegister'
import { handleNclick } from '@Helpers/nclick'
import { NCLICK } from '@Root/contants/lcs'

const cx = className.bind(style)

type Props = {
  house: MyhouseLabelType
}

// SortableHandle
const DragHandle = SortableHandle(() => (
  <div className={cx('button_item')} aria-label="등록한 부동산 순서 변경" role="button">
    <SvgMyhouseSettingDrag />
  </div>
))

const MyhouseSettingItem: React.FC<Props> = ({ house }: Props) => {
  const { open } = useModal()
  const { setRemoveHouseNo } = useSetting()

  function handleModifyClick(e: React.MouseEvent<HTMLElement>) {
    handleNclick(NCLICK.MY_HOUSE.SETTING.EDIT, e)

    if (isAuthProgress) {
      open(MODAL_TYPE.AUTH_MODIFY_LIMITED)
      e.preventDefault()
    }
  }

  function handleRemoveClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    handleNclick(NCLICK.MY_HOUSE.SETTING.DELETE, e)

    setRemoveHouseNo(house.houseNo)
    open(MODAL_TYPE.DELETE_MYHOUSE, {
      nclickConfirm: NCLICK.MY_HOUSE.SETTING.POPUP.DELETE,
    })
  }

  function myhouseAuthHandler(e: React.MouseEvent<HTMLAnchorElement>) {
    handleNclick(isResidentAuthCompletion ? NCLICK.MY_HOUSE.SETTING.OWNER_AUTHENTICATION : NCLICK.MY_HOUSE.SETTING.RESIDENT_AUTHENTICATION, e)

    if (isAuthProgress) {
      open(MODAL_TYPE.AUTH_MODIFY_LIMITED)
      e.preventDefault()
    }
  }

  const isOwnerAuthProgress = [AUTH_STATUS.APPLY1, AUTH_STATUS.APPLY2].includes(house.ownerAuthStatus as AUTH_STATUS)
  const isResidentAuthProgress = [AUTH_STATUS.APPLY1, AUTH_STATUS.APPLY2].includes(house.residentAuthStatus as AUTH_STATUS)
  const isAuthProgress = isOwnerAuthProgress || isResidentAuthProgress

  const isOwnerAuthCompletion = house.ownerAuthStatus === AUTH_STATUS.COMPLETION
  const isResidentAuthCompletion = house.residentAuthStatus === AUTH_STATUS.COMPLETION
  const isAuthCompletion = isOwnerAuthCompletion || isResidentAuthCompletion

  // 우리집 인증하기 조건
  // 분앙권, 상가, 건물이 아니고, 매매인데 인증정보가 없는 경우 true
  // 매매인데 인증정보가 있는 경우 조건
  const isWillOwnerAuth = house.ownerAuth !== 'Y' && !['B01', 'B02', 'B03'].includes(house.realestateTypeCode) && house.tradeTypeCode === TRADE.DEAL

  // 전월세인데 인증정보가 없는 경우 true
  // 1차로 자가 인증한 경우 false
  // 전월세인데 인증정보가 있는 경우 조건
  // 부동산분류코드가 분양권인 경우
  const isWillResidentAuth =
    house.ownerAuth !== 'Y' &&
    house.residentAuth !== 'Y' &&
    ([TRADE.LEASE, TRADE.RENT].includes(house.tradeTypeCode as TRADE) || ['B01', 'B02', 'B03'].includes(house.realestateTypeCode))

  const isStoreOrBuilding = ['D02', 'D03'].includes(house.realestateTypeCode)

  return (
    <>
      <button className={cx('button_item')} aria-label="등록한 부동산 목록 삭제" type="button" onClick={(e) => handleRemoveClick(e)}>
        <SvgMyhouseSettingRemove />
      </button>

      <button className={cx('button_modify')} aria-label="부동산 수정하기">
        <strong className={cx('title')}>
          <span className={cx('text')}>{house.houseLabel}</span>
          <a href={`${LAND_SERVICE_PREFIX_URL.MYHOUSE_REGISTER}/main/${house.houseNo}`} className={cx('link_modify')} onClick={(e) => handleModifyClick(e)}>
            <SvgMyhouseSettingModify />
          </a>
        </strong>
        <span className={cx('info')}>{house.fullAdditionalInfo}</span>
        {house.residentAuth === 'Y' && house.ownerAuth === 'N' && (
          <span className={cx('auth')}>
            <SvgMyhouseSettingAuth />
            입주민인증
          </span>
        )}
        {house.ownerAuth === 'Y' && (
          <span className={cx('auth')}>
            <SvgMyhouseSettingAuth />
            집주인인증
          </span>
        )}
        {!isAuthProgress && !isOwnerAuthCompletion && isWillOwnerAuth && house.ownerAuthCnt !== 2 && (
          <a
            href={
              isResidentAuthCompletion
                ? `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${house.houseNo}/${MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.OWNER}/CERTIFICATE_OF_REALESTATE_REGISTER`
                : `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${house.houseNo}`
            }
            className={cx('auth', 'type_try')}
            onClick={myhouseAuthHandler}
          >
            {isResidentAuthCompletion ? '집주인 인증하기' : '우리집 인증하기'}
            <SvgMyhouseSettingAuthArrow />
          </a>
        )}
        {!isAuthProgress &&
          !isAuthCompletion &&
          ((!isWillOwnerAuth && isWillResidentAuth && house.residentAuthCnt !== 2) ||
            (house.tradeTypeCode === TRADE.DEAL && house.ownerAuthCnt === 2 && house.residentAuthCnt !== 2)) && (
            <a href={`${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${house.houseNo}`} className={cx('auth', 'type_try')} onClick={myhouseAuthHandler}>
              {/* 상가/건물인 경우 '우리집 인증하기'로 노출 */}
              {(isStoreOrBuilding && '우리집 인증하기') || '입주민 인증하기'}
              <SvgMyhouseSettingAuthArrow />
            </a>
          )}
        {house.residentAuth === 'Y' && isOwnerAuthProgress && (
          <span className={cx('auth', 'type_proceeding')}>
            집주인 인증 진행 중입니다.
            <br />
            (영업일 기준 1~3일 소요)
          </span>
        )}
        {house.residentAuth === 'N' && isAuthProgress && (
          <span className={cx('auth', 'type_proceeding')}>
            인증 진행 중입니다
            <br />
            (영업일 기준 1~3일 소요)
          </span>
        )}
      </button>
      <DragHandle />
    </>
  )
}

export default MyhouseSettingItem
